import {
  OFFER_ICON_VIEWBOX,
  offerIconShapes,
  type OfferIconShape,
} from "@shared/offerIcons";

/**
 * The television's renderer for the shared offer-icon catalogue (`POS-7`).
 *
 * The geometry is defined exactly once, in `@shared/offerIcons`, as plain data;
 * this is the thirty lines that turn it into SVG for one surface. The CMS has
 * its own (`src/client/components/offers/OfferIcon.tsx`) and the guest companion
 * will get a third.
 *
 * Why the JSX is not shared with them, restated where the second copy lives so
 * nobody "de-duplicates" it: `src/shared/` is compiled by THREE tsconfigs and
 * only `tsconfig.json` has a `jsx` option — the API server image and the
 * shipping `@nenda/foyer-shared` package both compile it with plain `tsc`. A
 * `.tsx` there passes `npm run check` and fails both CI build lanes with ~40 ×
 * `TS17004`, which is exactly how POS-5 found out. See
 * `docs/memory/global/pitfalls/shared-alias-build-lanes.md`.
 *
 * Line weight is 1.7 on a 24 grid at every size, matching `LauncherIcon`: an
 * offer's picker and the launcher dock beside it must not read as two design
 * systems. `currentColor` by default, because eight templates each have their
 * own ink and a colour baked in here would fight seven of them.
 */

interface Props {
  /** A catalogue key, or any stored string — unknown ones fall back. */
  name: string | null | undefined;
  size?: number;
  color?: string;
  stroke?: number;
}

export default function OfferIcon({
  name,
  size = 44,
  color = "currentColor",
  stroke = 1.7,
}: Props) {
  const line = {
    fill: "none",
    stroke: color,
    strokeWidth: stroke,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
  };

  const draw = (shape: OfferIconShape, i: number) => {
    switch (shape.kind) {
      case "rect":
        return (
          <rect
            key={i}
            x={shape.x}
            y={shape.y}
            width={shape.width}
            height={shape.height}
            rx={shape.rx}
            {...line}
          />
        );
      case "circle":
        return (
          <circle key={i} cx={shape.cx} cy={shape.cy} r={shape.r} {...line} />
        );
      case "dot":
        // The one FILLED primitive — see `OfferIconShape`.
        return (
          <circle
            key={i}
            cx={shape.cx}
            cy={shape.cy}
            r={shape.r}
            fill={color}
            stroke="none"
          />
        );
      default:
        return <path key={i} d={shape.d} {...line} />;
    }
  };

  return (
    <svg
      width={size}
      height={size}
      viewBox={OFFER_ICON_VIEWBOX}
      // The label beside it always carries the meaning on this surface, so the
      // glyph is decoration to a screen reader rather than a second, worse copy
      // of the same word.
      aria-hidden="true"
      style={{ display: "block", flex: "0 0 auto" }}
    >
      {offerIconShapes(name).map(draw)}
    </svg>
  );
}
